import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getActionStatus } from "@/lib/aiActions";
import ActionResultChip from "./ActionResultChip";

// Restores stored action status only. Never calls propose/confirm/cancel.
export default function RecoveredActionCard({ reference, originWorkspace, originActor, renderReadResult }) {
  const { user, activeWorkspace } = useAuth();
  const sequence = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);
  const actionId = reference?.action_row_id;
  const capability = reference?.action_id;

  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    setError("");
    setRecord(null);
    try {
      if (!actionId || !capability || !originWorkspace || !originActor ||
          originActor !== user?.id || originWorkspace !== (activeWorkspace || user?.id)) {
        throw new Error("Return to the original account and workspace to check this action.");
      }
      const data = await getActionStatus(actionId, originWorkspace);
      if (request !== sequence.current) return;
      if (data?.id !== actionId || data?.action_id !== capability ||
          data?.workspace_owner !== originWorkspace || data?.actor_user_id !== originActor ||
          !["awaiting_confirmation", "authorized", "executing", "succeeded", "failed", "cancelled", "expired"].includes(data?.state)) {
        throw new Error("The saved action status could not be verified.");
      }
      setRecord(data);
    } catch (failure) {
      if (request === sequence.current) {
        setError(failure?.response?.status === 404
          ? "This action record is no longer available. That does not confirm whether it ran. Check the affected record before making another request."
          : "The action status could not be verified. Check the affected record before attempting the action again.");
      }
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [actionId, capability, originWorkspace, originActor, activeWorkspace, user?.id]);

  useEffect(() => {
    refresh();
    return () => { sequence.current += 1; };
  }, [refresh]);

  const terminal = record && ["succeeded", "failed", "cancelled", "expired"].includes(record.state);
  return (
    <div className="mt-2 rounded-lg border border-neutral-200 bg-white p-3 space-y-2"
      data-testid="ai-recovered-action-card">
      <p className="text-sm font-medium">{reference?.description || "Saved action"}</p>
      {loading && <p role="status" className="text-xs text-neutral-600">Checking saved action status…</p>}
      {error && <p role="alert" className="text-xs text-amber-800">{error}</p>}
      {!loading && terminal && <ActionResultChip state={record.state} />}
      {!loading && ["authorized", "executing"].includes(record?.state) &&
        <p className="text-xs text-amber-800">Execution has no final recorded outcome yet. Check again before attempting anything else.</p>}
      {!loading && record?.state === "awaiting_confirmation" &&
        <p className="text-xs text-neutral-700">This saved proposal is awaiting approval. To run an action after returning to this conversation, request a new proposal and review it before approving.</p>}
      {record?.state === "succeeded" && record.result &&
        ["projects.read", "finance.invoice.read", "sales.lead.read"].includes(capability) &&
        typeof renderReadResult === "function" && (
          <div className="text-sm" data-testid="ai-recovered-read-result">
            <p className="mb-1 text-xs text-neutral-500">Saved result from the original request; refresh status does not rerun the data query.</p>
            {renderReadResult(record.result, capability)}
          </div>
        )}
      {record?.payload && <details className="text-xs text-neutral-600">
        <summary>Saved action details</summary>
        <pre className="mt-1 whitespace-pre-wrap break-all">{JSON.stringify(record.payload, null, 2)}</pre>
      </details>}
      <button type="button" disabled={loading} onClick={refresh}
        className="text-xs underline disabled:opacity-50" data-testid="ai-recovered-action-refresh">
        {loading ? "Checking…" : "Refresh saved status"}
      </button>
    </div>
  );
}
